import React, { Fragment, useMemo } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import { NutritionCalculator } from '@vmos2/utils';

import type { NutritionType, ModeType } from 'types';
import { MacrosItem } from './macrosItem';

type Props = {
  calories: ?number,
  nutrition: NutritionType,
  mode: ModeType,
};

export const Macros = ({ calories, nutrition, mode }: Props) => {
  const { t } = useTranslation('nutrition');
  const theme = useTheme();

  const { carbs = 0, fats = 0, proteins = 0 } = nutrition;

  const { carbsPercentage, fatsPercentage, proteinsPercentage } = useMemo(
    () => NutritionCalculator.calculateNutritionComponentsPercentage(nutrition),
    [fats, proteins, carbs]
  );

  return (
    <Fragment>
      <MacrosTitle>{t('macros')}</MacrosTitle>
      <MacrosList>
        <MacrosItem
          name={t('carbsTitle')}
          progressValue={carbsPercentage}
          nutritionValue={carbsPercentage}
          mode={mode}
          progressColor={theme.color.nutrition.carbs}
        />
        <MacrosItem
          name={t('fatsTitle')}
          progressValue={fatsPercentage}
          nutritionValue={fatsPercentage}
          mode={mode}
          progressColor={theme.color.nutrition.fats}
        />
        <MacrosItem
          name={t('proteinsTitle')}
          progressValue={proteinsPercentage}
          nutritionValue={proteinsPercentage}
          progressColor={theme.color.nutrition.proteins}
          mode={mode}
        />
      </MacrosList>
      <Kcals mode={mode}>
        <KcalsValue>{NutritionCalculator.formatNutritionComponent(calories)}</KcalsValue>
        <span>{t('caloriesTitle')}</span>
      </Kcals>
    </Fragment>
  );
};

const MacrosTitle = styled('h3')`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  margin: 0 0 4px;
`;

const MacrosList = styled('div')`
  margin-bottom: 8px;
`;

const Kcals = styled('div')`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ mode, theme }) => (mode === 'dark' ? theme.color.lightGrey : theme.color.grey)};
  margin-bottom: 24px;
`;

const KcalsValue = styled('span')`
  margin-right: 4px;
`;
